import { CONTENT_SECTIONS } from '../sections';
import { COVERAGE_INCLUSION } from '../../constants';
import { TILE_VARIANTS } from '../../../../ahm-constants/ui';
import carOfferStripImage from './images/car-offer-strip.svg';
import claimTileImage from '../../../../images/claim-tile.svg';
import constants from '../../../../ahm-constants';
import getCommonQuestionsUrl from '../../utils/get-common-questions-url';
import helpTileImage from '../../../../images/help-tile.svg';
import insertFootnoteReference, { FOOTNOTE_PLACEHOLDER } from '../../../../utils/insert-footnote-reference';

const COVERAGE_FEATURES = {
  COLLISION_DAMAGE: 'collision-damage',
  FIRE_DAMAGE: 'fire-damage',
  FIXED_KILOMETRE_PLAN: 'fixed-kilometre-plan',
  MALICIOUS_ACT_DAMAGE: 'malicious-act-damage',
  NATURAL_EVENT_DAMAGE: 'natural-event-damage',
  PROPERTY_DAMAGE: 'property-damage',
  THEFT: 'theft',
};

const roadsideInclusion = `Essential roadside assistance to get you back on the road.

This is a separate service provided to you by Digicall Assist. Read the Roadside Assistance [Terms & Conditions](https://help.ahm.com.au/hc/en-us/articles/360001775316)`;

export default {
  meta: {
    description:
      'Fast and easy car insurance that gets you moving right away. You’ll also save up to 15% as an ahm or Medibank health member. T&Cs apply.',
    title: 'Car insurance',
  },
  sections: [
    {
      type: CONTENT_SECTIONS.HEADER,
    },
    {
      type: CONTENT_SECTIONS.PROMOTIONS,
    },
    {
      footnote: {
        linkId: 'footnote-1',
        reference: '1',
      },
      image: {
        alt: 'car keys',
        src: carOfferStripImage.src,
      },
      title: 'ahm and Medibank health members get up to 15% off!',
      type: CONTENT_SECTIONS.OFFER_STRIP,
    },
    {
      type: CONTENT_SECTIONS.VALUE_PROPOSITIONS,
    },
    {
      cta: {
        name: 'Start a quote',
        url: `${constants.URLS.SALES.CAR_INSURANCE}/quote`,
      },
      footnote: {
        linkId: 'footnote-2',
        reference: '2',
      },
      groups: [
        {
          addons: [
            {
              description: roadsideInclusion,
              title: 'Roadside assistance',
            },
            {
              description: 'Cover if your vehicle is damaged or lost as a result of theft, attempted theft or fire.',
              title: 'Fire and theft cover',
            },
          ],
          contentTitle: 'Third party property cover',
          description: 'Covers damage your car might cause to another person’s car or property.',
          id: 'third-party-cover',
          inclusions: [
            {
              description: 'You can choose to opt for a higher excess and reduce your premiums.',
              title: 'Flexi-excess',
            },
            {
              description: 'We’ll pay up to $20 million if your car damages someone else’s vehicle or property.',
              title: 'Up to $20m liability cover',
            },
            {
              description:
                'Call us any time. We’re always here to help you stay safe and organise your car to be towed, no matter the time.',
              title: '24/7 emergency help',
            },
            {
              description:
                'Cover up to $3,000 if your vehicle is damaged by an uninsured driver at fault who you can identify.',
              title: 'Damage by uninsured drivers',
            },
          ],
          tabTitle: 'Third party property',
        },
        {
          addons: [
            {
              callout: 'Cheaper!',
              description: 'Simple cover for when you don’t spend too much time behind the wheel.',
              title: 'Pay As You Drive Cover',
            },
            {
              description: roadsideInclusion,
              title: 'Roadside assistance',
            },
            {
              description: 'We’ll help with the bill for a hire car if your claim for theft or damage is covered.',
              title: 'Hire car',
            },
            {
              description:
                'We’ll repair or replace your car, excess-free, for your first animal collision claim each year.',
              title: 'Kanga cover',
            },
            {
              description: 'Up to $3,000 to cover your sports gear while it’s in, or on, your car. Cover terms apply.',
              title: 'Sports gear cover',
            },
            {
              description: 'We’ll repair or replace your windscreen, excess-free, once every 12 months.',
              title: 'Excess-free windscreen',
            },
          ],
          contentTitle: 'Comprehensive cover',
          description: 'Protection for your car, and cover in case you damage someone else’s car or property.',
          id: 'comprehensive-cover',
          inclusions: [
            {
              description: 'You can choose to opt for a higher excess and reduce your premiums.',
              title: 'Flexi-excess',
            },
            {
              description: 'We’ll pay up to $20 million if your car damages someone else’s vehicle or property.',
              title: 'Up to $20m liability cover',
            },
            {
              description:
                'Call us any time. We’re always here to help you stay safe and organise your car to be towed, no matter the time.',
              title: '24/7 emergency help',
            },
            {
              description: 'Cover for trailers, caravans, personal property, child safety seats and more.',
              title: 'Additional benefits',
            },
            {
              description:
                'Cover for damage or loss caused by theft, attempted theft, collision, malicious damage, fire, and natural events.',
              title: 'Theft and damage cover',
            },
            {
              description:
                'We’ll replace your car with a new car of the same value if it’s under 2 years old, has been written off and you’re the first registered owner.',
              title: 'New for old car replacement',
            },
          ],
          tabTitle: 'Comprehensive',
        },
        {
          contentTitle: 'Compare cover',
          covers: [
            {
              features: [
                {
                  id: COVERAGE_FEATURES.PROPERTY_DAMAGE,
                  inclusionState: COVERAGE_INCLUSION.INCLUDED,
                },
                {
                  id: COVERAGE_FEATURES.COLLISION_DAMAGE,
                  inclusionState: COVERAGE_INCLUSION.EXCLUDED,
                },
                {
                  id: COVERAGE_FEATURES.NATURAL_EVENT_DAMAGE,
                  inclusionState: COVERAGE_INCLUSION.EXCLUDED,
                },
                {
                  id: COVERAGE_FEATURES.MALICIOUS_ACT_DAMAGE,
                  inclusionState: COVERAGE_INCLUSION.EXCLUDED,
                },
                {
                  id: COVERAGE_FEATURES.THEFT,
                  inclusionState: COVERAGE_INCLUSION.OPTIONAL,
                },
                {
                  id: COVERAGE_FEATURES.FIRE_DAMAGE,
                  inclusionState: COVERAGE_INCLUSION.OPTIONAL,
                },
                {
                  id: COVERAGE_FEATURES.FIXED_KILOMETRE_PLAN,
                  inclusionState: COVERAGE_INCLUSION.EXCLUDED,
                },
              ],
              id: 'third-party-cover',
              name: 'Third party property',
            },
            {
              features: [
                {
                  id: COVERAGE_FEATURES.PROPERTY_DAMAGE,
                  inclusionState: COVERAGE_INCLUSION.INCLUDED,
                },
                {
                  id: COVERAGE_FEATURES.COLLISION_DAMAGE,
                  inclusionState: COVERAGE_INCLUSION.INCLUDED,
                },
                {
                  id: COVERAGE_FEATURES.NATURAL_EVENT_DAMAGE,
                  inclusionState: COVERAGE_INCLUSION.INCLUDED,
                },
                {
                  id: COVERAGE_FEATURES.MALICIOUS_ACT_DAMAGE,
                  inclusionState: COVERAGE_INCLUSION.INCLUDED,
                },
                {
                  id: COVERAGE_FEATURES.THEFT,
                  inclusionState: COVERAGE_INCLUSION.INCLUDED,
                },
                {
                  id: COVERAGE_FEATURES.FIRE_DAMAGE,
                  inclusionState: COVERAGE_INCLUSION.INCLUDED,
                },
                {
                  id: COVERAGE_FEATURES.FIXED_KILOMETRE_PLAN,
                  inclusionState: COVERAGE_INCLUSION.OPTIONAL,
                },
              ],
              id: 'comprehensive-cover',
              name: 'Comprehensive',
            },
          ],
          features: [
            {
              id: COVERAGE_FEATURES.PROPERTY_DAMAGE,
              name: 'You cause damage to another person’s property',
            },
            {
              id: COVERAGE_FEATURES.COLLISION_DAMAGE,
              name: 'Your vehicle suffers loss or damage as a result of a collision',
            },
            {
              id: COVERAGE_FEATURES.NATURAL_EVENT_DAMAGE,
              name: 'Your vehicle suffers loss or damage as a result of a natural event',
            },
            {
              id: COVERAGE_FEATURES.MALICIOUS_ACT_DAMAGE,
              name: 'Your vehicle suffers loss or damage as a result of a malicious act',
            },
            {
              id: COVERAGE_FEATURES.THEFT,
              name: 'Your vehicle is damaged or lost as a result of theft',
            },
            {
              id: COVERAGE_FEATURES.FIRE_DAMAGE,
              name: 'Your vehicle suffers loss or damage as a result of fire',
            },
            {
              id: COVERAGE_FEATURES.FIXED_KILOMETRE_PLAN,
              name: 'Pay As You Drive Cover',
            },
          ],
          id: 'compare',
          tabTitle: 'Compare cover',
        },
      ],
      title: 'Choose cover that works for you',
      type: CONTENT_SECTIONS.INCLUSIONS,
    },
    {
      tiles: [
        [
          {
            ctas: [
              {
                name: 'Make a claim',
                url: `${constants.URLS.SALES.CAR_INSURANCE}/login`,
              },
            ],
            footnote: {
              linkId: 'footnote-3',
              reference: '3',
            },
            image: {
              alt: 'hands exchanging claim form for money',
              src: claimTileImage.src,
            },
            subtitle: insertFootnoteReference({
              footnote: {
                reference: '3',
              },
              text: `Most claims can be lodged online 24/7${FOOTNOTE_PLACEHOLDER}. In some cases you’ll even get a decision within minutes.`,
            }),
            title: 'Easy claiming 24/7',
            variant: TILE_VARIANTS.VERTICAL_CENTER,
          },
          {
            ctas: [
              {
                name: 'More on car insurance',
                url: getCommonQuestionsUrl('car'),
              },
            ],
            image: {
              alt: 'man screaming for help',
              src: helpTileImage.src,
            },
            subtitle:
              'Find out more about our car insurance product, how to make a claim and how to manage your policy.',
            title: 'Need a quick answer?',
            variant: TILE_VARIANTS.VERTICAL_CENTER,
          },
        ],
      ],
      type: CONTENT_SECTIONS.TILES,
    },
    {
      type: CONTENT_SECTIONS.COMMON_QUESTIONS,
    },
    {
      type: CONTENT_SECTIONS.QUICK_LINKS,
    },
    {
      type: CONTENT_SECTIONS.TERMS_AND_CONDITIONS,
    },
  ],
  type: 'car',
};
